// src/App.jsx
import React, { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CartProvider } from "./Context/CartContext";
import { WishlistProvider } from "./Context/WishlistContext";
import { CurrencyProvider, useCurrency } from "./Context/CurrencyContext";
import Footer from "./components/Footer";
import Header from "./components/Header";
import ScrollToTopButton from "./components/ScrollToTopButton";

import Home from "./pages/Home/Home";
import About from "./pages/About/About";
import Contact from "./pages/Contact/Contact";
import LoginPage from "./pages/Login/LoginPage";
import SearchPage from "./pages/SearchPage/SearchPage";
import SearchResultsPage from "./pages/SearchPage/SearchResultsPage";
import VerificationPage from './pages/Login/VerificationPage';
import CheckoutPage from "./Context/CheckoutPage";
import HomepageBesseller from "./pages/Bestsellers/HomepageBesseller";
import ProductDetails from "./pages/Bestsellers/ProductDetails";
import ProductList from "./pages/Bestsellers/ProductList";
import YourDailyRoutine from "./pages/DailyRoutine/YourDailyRoutine";
import AccountLayout from "./components/AccountLayout";
import Profile from "./components/Profile";
import Orders from "./components/Orders";
import Wishlist from "./components/Wishlist";
import Currency from "./components/Currency";

import ReturnRefund from "./pages/ReturnRefund/ReturnRefund";
import Legal from "./pages/Legal/Legal";
import ShippingPolicy from "./pages/ShippingPolicy/ShippingPolicy";

// Protected Route Component
const ProtectedRoute = ({ children, requireAuth = false }) => {
  const isAuthenticated = localStorage.getItem("isLoggedIn") === "true";
  
  if (requireAuth && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// ScrollToTop Component
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Main AppContent component
function AppContent() {
  const location = useLocation();
  const { selectedCountry, setCurrencyOpen } = useCurrency();

  const handleCurrencyClick = () => {
    setCurrencyOpen(true);
  };

  return (
    <>
      <ScrollToTop />
      
      <Header />

      <Routes>
        {/* Main Pages */}
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/login" element={<LoginPage />} />
        {/* ✅ Search Results page MUST be before /search */}
        <Route path="/search/results" element={<SearchResultsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/verification" element={<VerificationPage />} />
        <Route path="/checkout" element={
          <ProtectedRoute requireAuth={true}>
            <CheckoutPage />
          </ProtectedRoute>
        } />
        <Route path="/bestsellers" element={<HomepageBesseller />} />
        <Route path="/products" element={<ProductList />} />
        <Route path="/product/:id" element={<ProductDetails />} />
        <Route path="/daily-routine" element={<YourDailyRoutine />} />
        
        {/* Policy Pages */}
        <Route path="/return-refund" element={<ReturnRefund />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/shipping-policy" element={<ShippingPolicy />} />
        
        {/* Account Pages */}
        <Route path="/wishlist" element={
          <ProtectedRoute requireAuth={true}>
            <Wishlist />
          </ProtectedRoute>
        } />
        
        <Route path="/account" element={<AccountLayout />}>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<Profile />} />
          <Route path="orders" element={<Orders />} />
          <Route path="currency" element={<Currency />} />
        </Route>

        {/* Order Details Route */}
        <Route path="/order/:orderId" element={
          <div className="order-details-page">
            <h2>Order Details</h2>
          </div>
        } />

        {/* Catch-all Route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      <Footer
        selectedCountry={selectedCountry}
        onCurrencyClick={handleCurrencyClick}
      />

      <ScrollToTopButton />
    </>
  );
}

// Main App component
function App() {
  return (
    <CartProvider>
      <WishlistProvider>
        <CurrencyProvider>
          <AppContent />
        </CurrencyProvider>
      </WishlistProvider>
    </CartProvider>
  );
}

export default App;